import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import firebase from '../firebase';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  font-family: 'Prompt', sans-serif;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-size: 25px;
  font-weight: 400;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9CA3AF;

  &:hover {
    color: #6B7280;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #686868;
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 16px;
  color: #374151;
  font-weight: 400;

  &::placeholder {
    color: #CFD3D4;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 16px;
  color: #374151;
  font-weight: 400;
  font-family: 'Prompt', sans-serif;
  resize: vertical;

  &::placeholder {
    color: #CFD3D4;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 16px;
  color: #374151;
  font-weight: 400;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ImageUploadSection = styled.div`
  margin-bottom: 2rem;
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadButton = styled.button`
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;
  color: #374151;
  font-weight: 400;
  display: inline-block;
  vertical-align: top;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 1rem;
  vertical-align: top;
`;

const ImagePreview = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  object-fit: cover;
  background-color: #F9FAFB;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #EF4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #DC2626;
  }
`;

const MapContainer = styled.div`
  margin-bottom: 1.5rem;
  height: 300px;
  background-color: #E5E7EB;
  border-radius: 6px;
  overflow: hidden;
`;

const NoticeText = styled.div`
  margin-bottom: 1.5rem;
  font-size: 14px;
  color: #686868;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #E5E7EB;
  position: sticky;
  bottom: 0;
  background-color: white;
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.875rem;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: #009B9E;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const EditCampaignManagementModal = ({ isOpen, onClose, onSave, campaignData }) => {
  const [formData, setFormData] = useState({
    campaignName: '',
    campaignDescription: '',
    serviceType: 'hospital',
    campaignDuration: '',
    startDate: '',
    endDate: '',
    hasCost: false,
    cost: '',
    promoCode: '',
    company: '',
    latitude: '',
    longitude: '',
    deliveryLocation: '',
    address: '',
    postalCode: '',
    subdistrictCode: '',
    note: ''
  });

  const [selectedCampaignImage, setSelectedCampaignImage] = useState(null);
  const [selectedCampaignIcon, setSelectedCampaignIcon] = useState(null);
  const [campaignImageFile, setCampaignImageFile] = useState(null);
  const [campaignIconFile, setCampaignIconFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Firebase Storage base URL
  const FIREBASE_STORAGE_BASE_URL = 'https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/';

  // ฟังก์ชันแปลง relative path เป็น full Firebase URL สำหรับแสดงรูป
  const expandToFullURL = (relativePath) => {
    if (!relativePath || relativePath.trim() === '') return '';

    // ถ้าเป็น full URL อยู่แล้ว ให้คืนค่ากลับไปตรงๆ
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }

    // ต่อ base URL เข้าไป
    return FIREBASE_STORAGE_BASE_URL + relativePath;
  };

  // ฟังก์ชันตัด base URL ออก เหลือแค่ relative path สำหรับบันทึกใน DB
  const extractRelativePath = (fullURL) => {
    if (!fullURL || fullURL.trim() === '') return '';

    // ถ้าเป็น full URL ให้ตัด base URL ออก
    if (fullURL.startsWith(FIREBASE_STORAGE_BASE_URL)) {
      return fullURL.replace(FIREBASE_STORAGE_BASE_URL, '');
    }

    // ถ้าเป็น relative path อยู่แล้ว ให้คืนค่ากลับไปตรงๆ
    return fullURL;
  };

  // โหลดข้อมูลแคมเปญเมื่อเปิด Modal
  useEffect(() => {
    if (campaignData && isOpen) {
      // แปลงวันที่จาก DD/MM/YYYY เป็น YYYY-MM-DD สำหรับ input
      const convertToInputDate = (dateStr) => {
        if (!dateStr || dateStr === '-') return '';
        try {
          if (dateStr.includes('/')) {
            const [day, month, year] = dateStr.split('/');
            return `${year}-${month}-${day}`;
          }
          return dateStr;
        } catch (e) {
          return dateStr;
        }
      };

      // ดึงตัวเลขจาก duration (เช่น "90 วัน" -> "90")
      const extractDuration = (durationStr) => {
        if (!durationStr || durationStr === '-') return '';
        return durationStr.replace(' วัน', '').trim();
      };

      setFormData({
        campaignName: campaignData.name || '',
        campaignDescription: campaignData.caption || '',
        serviceType: 'hospital',
        campaignDuration: extractDuration(campaignData.duration),
        startDate: convertToInputDate(campaignData.startDate),
        endDate: convertToInputDate(campaignData.endDate),
        hasCost: false,
        cost: '',
        promoCode: campaignData.promoCode || campaignData.promocode || '',
        company: campaignData.company || '',
        latitude: (campaignData.lat && campaignData.lat !== '0') ? campaignData.lat : '',
        longitude: (campaignData.lng && campaignData.lng !== '0') ? campaignData.lng : '',
        deliveryLocation: campaignData.address_name || '',
        address: campaignData.address || '',
        postalCode: campaignData.postcode || '',
        subdistrictCode: campaignData.district_code || '',
        note: campaignData.address_note || ''
      });

      // ⭐ Reset รูปภาพ/ไอคอนทุกครั้งก่อน แล้วค่อย set ใหม่ถ้ามี
      // เพื่อป้องกันรูปจากบริษัทก่อนหน้าค้างอยู่
      setSelectedCampaignImage(null);
      setSelectedCampaignIcon(null);
      setCampaignImageFile(null);
      setCampaignIconFile(null);

      // ถ้ามีรูปภาพ/ไอคอนจาก API ให้แสดง (ตรวจสอบว่าไม่ใช่ empty string)
      if (campaignData.image && campaignData.image.trim() !== '') {
        // แปลง relative path จาก backend เป็น full URL เพื่อแสดงรูป
        const fullImageURL = expandToFullURL(campaignData.image);
        setSelectedCampaignImage(fullImageURL);
      }

      if (campaignData.icon && campaignData.icon.trim() !== '') {
        // แปลง relative path จาก backend เป็น full URL เพื่อแสดงรูป
        const fullIconURL = expandToFullURL(campaignData.icon);
        setSelectedCampaignIcon(fullIconURL);
      }
    }
  }, [campaignData, isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCampaignImageFile(file);
      setSelectedCampaignImage(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setCampaignImageFile(null);
    setSelectedCampaignImage(null);
    const fileInput = document.getElementById('edit-image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCampaignIconFile(file);
      setSelectedCampaignIcon(URL.createObjectURL(file));
    }
  };

  const handleRemoveIcon = () => {
    setCampaignIconFile(null);
    setSelectedCampaignIcon(null);
    const fileInput = document.getElementById('edit-icon-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // อัปโหลดรูปภาพไป Firebase Storage
  const uploadImageToFirebase = async (file, folder = 'campaign-images') => {
    if (!file) return null;

    try {
      const timestamp = Date.now();

      // ดึงนามสกุลไฟล์ (เช่น .jpg, .png)
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));

      // สร้างชื่อไฟล์ใหม่เป็น mfu/timestamp + นามสกุล
      // เช่น: campaign-images/mfu-1767871313214.jpg
      const fileName = `${folder}/mfu-${timestamp}${fileExtension}`;

      const storageRef = firebase.storage().ref();
      const fileRef = storageRef.child(fileName);

      // อัปโหลดไฟล์
      await fileRef.put(file);

      // รับ URL
      const downloadURL = await fileRef.getDownloadURL();
      return downloadURL;
    } catch (error) {
      console.error('Error uploading to Firebase:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (onSave && campaignData) {
      setIsUploading(true); // เริ่มต้นการอัปโหลด
      try {
        // อัปโหลดรูปภาพและไอคอนไป Firebase (ถ้ามี)
        let imageURL = selectedCampaignImage;
        let iconURL = selectedCampaignIcon;

        if (campaignImageFile) {
          imageURL = await uploadImageToFirebase(campaignImageFile, 'campaign-images');
        }

        if (campaignIconFile) {
          iconURL = await uploadImageToFirebase(campaignIconFile, 'campaign-icons');
        }

        // ตัด base URL ออก เหลือแค่ relative path สำหรับบันทึกใน DB
        const imageRelativePath = extractRelativePath(imageURL);
        const iconRelativePath = extractRelativePath(iconURL);

        // แปลง formData ให้ตรงกับ API format ตาม collection
        const apiData = {
          id: campaignData.id, // ต้องมี id สำหรับการ update
          name: formData.campaignName,
          caption: formData.campaignDescription,
          image: imageRelativePath, // ส่งแค่ relative path (ตัด base URL ออกแล้ว)
          icon: iconRelativePath, // ส่งแค่ relative path (ตัด base URL ออกแล้ว)
          promocode: formData.promoCode,
          day: formData.campaignDuration || '0', // ระยะเวลาแคมเปญ (รายบุคคล)
        days: formData.campaignDuration || '0', // ลองส่งทั้ง day และ days
        datestart: formData.startDate || '0000-00-00', // วันที่เริ่มต้น
        datestop: formData.endDate || '0000-00-00', // วันที่สิ้นสุด
        lat: formData.latitude || '0',
        lng: formData.longitude || '0',
        address: formData.address,
        address_name: formData.deliveryLocation,
        address_note: formData.note,
        postcode: formData.postalCode || '0',
        district_code: formData.subdistrictCode || '0'
      };

      onSave(apiData);
      setIsUploading(false); // เสร็จสิ้นการอัปโหลด
      onClose();
      } catch (error) {
        console.error('❌ Error in handleSubmit:', error);
        setIsUploading(false); // หยุดการอัปโหลดเมื่อเกิดข้อผิดพลาด
        alert('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ กรุณาลองใหม่อีกครั้ง');
      }
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <ModalTitle>แก้ไขบริษัท</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        {/* Body */}
        <ModalBody>
          {/* ชื่อบริษัท */}
          <FormGroup>
            <Label>ชื่อบริษัท</Label>
            <Input
              type="text"
              value={formData.campaignName}
              onChange={(e) => handleInputChange('campaignName', e.target.value)}
              placeholder="ชื่อบริษัท"
            />
          </FormGroup>

          {/* รายละเอียดแคมเปญ */}
          <FormGroup>
            <Label>รายละเอียดแคมเปญ</Label>
            <TextArea
              value={formData.campaignDescription}
              onChange={(e) => handleInputChange('campaignDescription', e.target.value)}
              placeholder="รายละเอียดแคมเปญ"
              rows={3}
            />
          </FormGroup>

          {/* เลือกรูปภาพ */}
          <ImageUploadSection>
            <ImageUploadInput
              type="file"
              id="edit-image-upload"
              accept="image/*"
              onChange={handleImageUpload}
            />
            <ImageUploadButton onClick={() => document.getElementById('edit-image-upload').click()}>
              เลือกรูปภาพ
            </ImageUploadButton>
            {selectedCampaignImage && (
              <ImagePreviewContainer>
                <ImagePreview src={selectedCampaignImage} alt="Campaign Preview" />
                <RemoveImageButton onClick={handleRemoveImage}>×</RemoveImageButton>
              </ImagePreviewContainer>
            )}
          </ImageUploadSection>

          {/* ระยะแคมเปญกรณีรายบุคคล */}
          {/* <FormGroup>
            <Label>ระยะแคมเปญกรณีรายบุคคล</Label>
            <Select
              value={formData.campaignDuration}
              onChange={(e) => handleInputChange('campaignDuration', e.target.value)}
            >
              <option value="">เลือกจำนวนวัน</option>
              <option value="7">7 วัน</option>
              <option value="14">14 วัน</option>
              <option value="30">30 วัน</option>
              <option value="60">60 วัน</option>
              <option value="90">90 วัน</option>
            </Select>
          </FormGroup> */}

          {/* วันที่เริ่มต้น และ วันที่สิ้นสุด */}
          {/* <GridContainer>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>วันที่เริ่มต้น ค.ศ. (ปปปป-ดด-วว)</Label>
              <Input
                type="text"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                placeholder="2024-01-01"
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>วันที่สิ้นสุด ค.ศ. (ปปปป-ดด-วว)</Label>
              <Input
                type="text"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                placeholder="2024-12-31"
              />
            </FormGroup>
          </GridContainer> */}

          {/* Google Map */}
          <MapContainer>
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${formData.latitude || '20.031170908430916'},${formData.longitude || '99.87735115782249'}&zoom=15`}
              width="100%"
              height="100%"
              style={{ border: 0, pointerEvents: 'auto' }}
              allowFullScreen=""
              loading="lazy"
              title="Google Map Location"
            />
          </MapContainer>

          {/* ข้อความแจ้งเตือน */}
          <NoticeText>
            ระบบจำเป็นต้องขอที่อยู่จัดส่งยาจากท่านเพื่อจัดหาจุดบริการที่ใกล้เคียงกับท่านมากที่สุด
          </NoticeText>

          {/* ละติจูด และ ลองจิจูด */}
          <GridContainer>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>ละติจูด</Label>
              <Input
                type="text"
                value={formData.latitude}
                onChange={(e) => handleInputChange('latitude', e.target.value)}
                placeholder="20.031170908430916"
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>ลองจิจูด</Label>
              <Input
                type="text"
                value={formData.longitude}
                onChange={(e) => handleInputChange('longitude', e.target.value)}
                placeholder="99.87735115782249"
              />
            </FormGroup>
          </GridContainer>

          {/* หมายเหตุ */}
          <FormGroup>
            <Label>หมายเหตุ</Label>
            <TextArea
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              placeholder="หมายเหตุ"
              rows={3}
            />
          </FormGroup>
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <SubmitButton onClick={handleSubmit} disabled={isUploading}>
            {isUploading ? 'กำลังอัปโหลดรูปภาพ...' : 'บันทึก'}
          </SubmitButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default EditCampaignManagementModal;
